import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';

import { computed, nextTick, ref, toValue, watch } from 'vue';

export interface UseDecimalFieldOptions {
  /** Reactive source value */
  modelValue?: MaybeRefOrGetter<number | null | undefined>;
  /** Maximum number of digits before decimal point */
  integerDigits?: number;
  /** Number of decimal places */
  decimals?: number;
  /** Callback for value updates */
  onUpdate?: (value: number | null) => void;
}

export interface UseDecimalFieldComposable {
  /** Current display value */
  displayValue: Ref<string>;
  /** Error state */
  isError: Ref<boolean>;
  /** Computed field value */
  fieldValue: ComputedRef<number | null>;
  /** Handle keypress event */
  handleKeypress: (event: KeyboardEvent) => void;
  /** Handle input event */
  handleInput: (event: Event) => void;
}

function toDisplay(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * Composable for handling decimal number input fields
 */
export default function useDecimalField(
  options: UseDecimalFieldOptions = {},
): UseDecimalFieldComposable {
  const {
    modelValue,
    integerDigits = 5,
    decimals = 2,
    onUpdate = () => {
      /* noop */
    },
  } = options;

  const displayValue = ref(toDisplay(toValue(modelValue)));
  const isError = ref(false);

  watch(
    () => toValue(modelValue),
    (newValue) => {
      // Skip if the incoming value already equals what's parsed from the
      // current display string — protects mid-edit states like "1." or
      // "1.50" from collapsing when the parent re-emits the same number.
      const current = Number(displayValue.value);
      if (
        displayValue.value !== '' &&
        !isNaN(current) &&
        newValue === current
      ) {
        return;
      }
      displayValue.value = toDisplay(newValue);
    },
  );

  const fieldValue = computed({
    get: () => {
      const value = Number(displayValue.value);
      return isNaN(value) ? null : value;
    },
    set: (value: number | null) => {
      displayValue.value = value === null ? '' : String(value);
      onUpdate(value);
    },
  });

  function handleKeypress(event: KeyboardEvent): void {
    // Ignore special keys
    if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    // Allow only digits and dot
    if (!/[\d.]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const value = input.value;
    const dotPosition = value.indexOf('.');

    // Prevent second dot
    if (event.key === '.' && dotPosition !== -1) {
      event.preventDefault();
      return;
    }

    // Check if we're adding a digit before decimal point
    if (
      event.key !== '.' &&
      (dotPosition === -1 || cursorPosition <= dotPosition)
    ) {
      const integerPart =
        dotPosition === -1 ? value : value.slice(0, dotPosition);
      if (integerPart.length >= integerDigits) {
        event.preventDefault();
        return;
      }
    }

    // Check if we're adding a digit after decimal point
    if (dotPosition !== -1 && cursorPosition > dotPosition) {
      const decimalPart = value.slice(dotPosition + 1);
      if (decimalPart.length >= decimals) {
        event.preventDefault();
      }
    }
  }

  function handleInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Remove all characters except digits and dots
    value = value.replace(/[^\d.]/g, '');

    // Handle multiple dots
    const dots = value.match(/\./g);
    if (dots && dots.length > 1) {
      value = value.replace(/\./g, (match, index) =>
        index === value.indexOf('.') ? match : '',
      );
    }

    // Split into integer and decimal parts
    const [integerPart, decimalPart = ''] = value.split('.');

    // Apply limits
    value =
      integerPart.slice(0, integerDigits) +
      (value.includes('.') ? '.' + decimalPart.slice(0, decimals) : '');

    // Update display value
    displayValue.value = value;

    // Try to restore cursor position
    const newPosition = Math.min(cursorPosition, value.length);
    void nextTick(() => {
      input.setSelectionRange(newPosition, newPosition);
    });

    // Update model value
    if (value && value !== '.') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        onUpdate(numValue);
      }
    } else {
      onUpdate(null);
    }
  }

  return {
    displayValue,
    isError,
    fieldValue,
    handleKeypress,
    handleInput,
  };
}
